import React, { createContext, useContext, useState, useEffect } from 'react';
import { showErrorToast } from '@/components/toast/globalToast';
import { User, AuthContextType } from '@/types/auth';
import AuthService from '@/services/api/auth';
import { setBearerTokenFromLocalStorage } from '@/utils/axios';

const parseJwt = (token: string): User | null => {
  try {
    const base64Payload = token.split('.')[1];
    const payload = JSON.parse(atob(base64Payload));
    return {
      uid: payload.sub || payload.id || payload.uid,
      email: payload.email || null,
      displayName: payload.name || payload.displayName || payload.email || null,
      name: payload.name || payload.displayName || payload.email || null,
      photoURL: payload.picture || payload.photoURL || null,
      role: payload.role,
    };
  } catch {
    return null;
  }
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = !!token;

  useEffect(() => {
    const init = async () => {
      const stored = localStorage.getItem('token');
      if (stored) {
        setToken(stored);
        setBearerTokenFromLocalStorage();
        try {
          const profile = await AuthService.getMe();
          setUser(profile);
          localStorage.setItem('user', JSON.stringify(profile));
        } catch {
          const cached = localStorage.getItem('user');
          setUser(cached ? JSON.parse(cached) : parseJwt(stored));
        }
      }
      setIsLoading(false);
    };
    init();
  }, []);

  const signIn = async (
    identifier: string,
    credential: string
  ): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const backendToken = await AuthService.login(identifier, credential);
      setToken(backendToken);
      setBearerTokenFromLocalStorage();
      try {
        const profile = await AuthService.getMe();
        setUser(profile);
        localStorage.setItem('user', JSON.stringify(profile));
      } catch {
        setUser(parseJwt(backendToken));
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        (err instanceof Error ? err.message : 'Invalid email or password');
      setError(errorMessage);
      showErrorToast('Login Failed', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async (): Promise<void> => {
    setToken(null);
    setUser(null);
    AuthService.logout(); // clears localStorage
  };

  const clearError = (): void => setError(null);

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    error,
    isAuthenticated,
    signIn,
    signOut,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
